import React, { useState } from "react";
import { components } from "react-select";
import { CloseIcon, SearchIcon } from "@boi/react-icons";
import solasTokens from "@boi/design-tokens/dist/web/boi/light/tokens.json";
import Select from "./select_old";

import { iChoice, iControlProps } from "../types";

type SearchListProps = iControlProps & {
    referenceList: string;
    referenceColumn: string;
    placeholder?: string;
    inputValue?: string;
    isLoading?: boolean;
    options?: iChoice[];
};

const DropdownIndicator = (props: any) => {
    return (
        components.DropdownIndicator && (
            <components.DropdownIndicator {...props} className="no-animation">
                <SearchIcon
                    width={24}
                    height={24}
                    style={{ cursor: 'pointer', color: solasTokens.themeColor.base500 }}
                />
            </components.DropdownIndicator>
        )
    );
};

const ClearIndicator = (props: any) => {
    return (
        components.ClearIndicator && (
            <components.ClearIndicator
                {...props}
                clearValue={() => props.selectValue.onChange("")}
            >
                <CloseIcon
                    width={24}
                    height={24}
                    style={{ cursor: 'pointer'}}
                />
            </components.ClearIndicator>
        )
    );
};

const NoOptionsMessage = (props: any) => {
    return (
        <components.NoOptionsMessage {...props}>
            <span style={{ color: solasTokens.themeColor.base500 }}>
                Sorry, we can&apos;t find a match for that. Please try again.
            </span>
        </components.NoOptionsMessage>
    );
};

const SearchList = ({ options, ...props }: SearchListProps) => {
    const { referenceList, referenceColumn, value, onChange } = props;
    const [inputValue, setInputValue] = useState<string>(
        props.inputValue || ""
    );
    const [isLoading, setIsLoading] = useState<boolean>(
        props.isLoading || false
    );
    const [choiceList, setChoiceList] = useState<iChoice[]>(options || []);

    const fetchController = new AbortController();

    const loadOptions = async (searchValue = "") => {
        if (searchValue.length < 2) {
            setChoiceList([]);
            return;
        }

        isLoading && fetchController.abort();

        const baseURL = `${process.env.REACT_APP_API_TABLES}/searchList`;
        const searchParams = {
            tableName: referenceList,
            column: referenceColumn,
            searchValue: searchValue
        };
        const fetchURL = `${baseURL}?${new URLSearchParams(searchParams)}`;

        setIsLoading(true);

        await fetch(fetchURL, {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            },
            signal: fetchController.signal
        })
            .then(response => response.json())
            .then(data => {
                const options: iChoice[] = [];

                data.result &&
                    data.result.forEach((row: { [key: string]: string }) => {
                        return options.push({
                            value: row[referenceColumn],
                            text: row[referenceColumn]
                        });
                    });
                setIsLoading(false);
                setChoiceList(options);
            })
            .catch(error => {
                if (error.name !== "AbortError") {
                    console.error(error);
                    setIsLoading(false);
                }
            });
    };

    return (
        <Select
            {...props}
            placeholder={props.placeholder || "Type to search"}
            choices={choiceList}
            inputValue={inputValue}
            onInputChange={(value: string) => {
                setInputValue(value);
                loadOptions(value);
            }}
            onChange={(value: any) => {
                onChange && onChange(value);

                // Clear options list on clear event
                !value && setChoiceList([]);
            }}
            customComponents={{
                DropdownIndicator,
                ClearIndicator,
                NoOptionsMessage
            }}
            styles={{
                //@ts-ignore
                option: (styles, { data, isDisabled, isFocused, isSelected, isActive }) => {
                    return {
                      ...styles,
                      backgroundColor: isDisabled
                        ? solasTokens.themeColor.base150
                        : isFocused
                        ? solasTokens.themeColor.base150
                        : undefined,
                      color: isDisabled
                        ? solasTokens.themeColor.base400
                        : solasTokens.themeColor.base500,
                      cursor: isDisabled ? 'not-allowed' : 'default',
                    };
                },
                //@ts-ignore
                control: (styles, { isFocused }) => {
                    return {
                        ...styles,
                        borderWidth: 2,
                        paddingTop: 8,
                        paddingBottom: 8,
                        paddingLeft: 40,
                        paddingRight: 48,
                        borderColor: isFocused ? solasTokens.themeColor.core125 : solasTokens.themeColor.base400,
                        borderRadius: 8,
                        position: 'relative',

                        ':hover': {
                            ...styles[':hover'],
                            borderColor: solasTokens.themeColor.core125
                        }
                    }
                },
                //@ts-ignore
                clearIndicator: (styles) => ({
                    ...styles,
                    color: solasTokens.themeColor.core100,
                    padding: 0,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: solasTokens.themeColor.base150
                    },
                    position: 'absolute',
                    right: 16,
                    ':hover': {
                        ...styles[':hover'],
                        color: solasTokens.themeColor.core100
                    }
                }),
                //@ts-ignore
                dropdownIndicator: (styles) => ({
                    ...styles,
                    padding: '0 8px',
                    marginRight: 'auto',
                    marginLeft: '0',
                    position: 'absolute',
                    left: 0,
                  }),
              }}
            isLoading={isLoading}
            isSearchable={true}
            isClearable={!!value}
            openMenuOnClick={!!choiceList.length}
        />
    );
};

export default SearchList;